"use client"
// IndexedDB-backed DPoP key store.
// Stores the private CryptoKey with extractable:false — XSS cannot export raw key bytes.
// The public JWK is mirrored in localStorage for fast reads (it's public; exposure is harmless).

const DB_NAME = 'fexr-dpop'
const STORE_NAME = 'keys'
const PRIVATE_KEY_ID = 'signing-key'
const PUBLIC_JWK_LS = 'dpop_public_key_jwk'

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1)
    req.onupgradeneeded = () => req.result.createObjectStore(STORE_NAME)
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

function idbGet(db: IDBDatabase, key: string): Promise<CryptoKey | undefined> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).get(key)
    req.onsuccess = () => resolve(req.result as CryptoKey | undefined)
    req.onerror = () => reject(req.error)
  })
}

function idbPut(db: IDBDatabase, key: string, value: CryptoKey): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).put(value, key)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

function idbDelete(db: IDBDatabase, key: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const req = db.transaction(STORE_NAME, 'readwrite').objectStore(STORE_NAME).delete(key)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export interface DPoPKeyPair {
  privateKey: CryptoKey   // non-extractable — can only be used for signing
  publicKey: CryptoKey
  publicKeyJWK: JsonWebKey
}

/** Generate and persist a new DPoP keypair. Private key is non-extractable. */
export async function generateAndStoreDPoPKeyPair(): Promise<DPoPKeyPair> {
  const keyPair = await crypto.subtle.generateKey(
    { name: 'ECDSA', namedCurve: 'P-256' },
    false,  // non-extractable private key
    ['sign', 'verify']
  )
  const publicKeyJWK = await crypto.subtle.exportKey('jwk', keyPair.publicKey) as JsonWebKey
  publicKeyJWK.alg = 'ES256'
  publicKeyJWK.use = 'sig'

  const db = await openDB()
  await idbPut(db, PRIVATE_KEY_ID, keyPair.privateKey)
  db.close()

  localStorage.setItem(PUBLIC_JWK_LS, JSON.stringify(publicKeyJWK))

  return { privateKey: keyPair.privateKey, publicKey: keyPair.publicKey, publicKeyJWK }
}

/** Load existing keypair from IndexedDB + localStorage. Returns null if not found. */
export async function loadDPoPKeyPair(): Promise<DPoPKeyPair | null> {
  try {
    const storedPublicStr = localStorage.getItem(PUBLIC_JWK_LS)
    if (!storedPublicStr) return null

    const publicKeyJWK = JSON.parse(storedPublicStr) as JsonWebKey

    const db = await openDB()
    const privateKey = await idbGet(db, PRIVATE_KEY_ID)
    db.close()

    if (!privateKey) return null

    const publicKey = await crypto.subtle.importKey(
      'jwk',
      publicKeyJWK,
      { name: 'ECDSA', namedCurve: 'P-256' },
      true,
      ['verify']
    )

    return { privateKey, publicKey, publicKeyJWK }
  } catch {
    return null
  }
}

/** Load existing keypair or generate a new one. */
export async function getOrCreateDPoPKeyPair(): Promise<DPoPKeyPair> {
  const existing = await loadDPoPKeyPair()
  if (existing) return existing
  return generateAndStoreDPoPKeyPair()
}

/** Delete keypair from IndexedDB and localStorage. Called on sign-out. */
export async function clearDPoPKeyPair(): Promise<void> {
  try {
    const db = await openDB()
    await idbDelete(db, PRIVATE_KEY_ID)
    db.close()
  } catch { /* best-effort */ }
  localStorage.removeItem(PUBLIC_JWK_LS)
  localStorage.removeItem('dpop_private_key')  // remove any legacy extractable copy
}

/**
 * Sign a DPoP proof JWT using the stored non-extractable private key.
 * Uses WebCrypto directly — no need to export the key.
 */
export async function signDPoPProof(
  htm: string,
  htu: string,
  accessToken?: string,
  privateKey?: CryptoKey,
  publicKeyJWK?: JsonWebKey,
): Promise<string> {
  let key = privateKey
  let pubJwk = publicKeyJWK

  if (!key || !pubJwk) {
    const pair = await getOrCreateDPoPKeyPair()
    key = pair.privateKey
    pubJwk = pair.publicKeyJWK
  }

  const header = { alg: 'ES256', typ: 'dpop+jwt', jwk: pubJwk }
  const now = Math.floor(Date.now() / 1000)

  const payload: Record<string, unknown> = {
    jti: crypto.randomUUID(),
    htm: htm.toUpperCase(),
    htu,
    iat: now,
    exp: now + 60,
  }

  if (accessToken) {
    const hashBytes = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(accessToken)))
    let hashBin = ''
    for (let i = 0; i < hashBytes.length; i++) hashBin += String.fromCharCode(hashBytes[i]!)
    payload.ath = btoa(hashBin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
  }

  const enc = (s: string) =>
    btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')

  const headerB64 = enc(JSON.stringify(header))
  const payloadB64 = enc(JSON.stringify(payload))
  const signingInput = new TextEncoder().encode(`${headerB64}.${payloadB64}`)

  // ECDSA sign — raw r||s bytes (64 bytes for P-256)
  const sigDer = new Uint8Array(
    await crypto.subtle.sign({ name: 'ECDSA', hash: 'SHA-256' }, key, signingInput)
  )

  let sigBin = ''
  for (let i = 0; i < sigDer.length; i++) sigBin += String.fromCharCode(sigDer[i]!)
  const sigB64 = btoa(sigBin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')

  return `${headerB64}.${payloadB64}.${sigB64}`
}
